import AddIcon from '@mui/icons-material/Add';
import { i18n } from 'i18n';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import RemoveIcon from '@mui/icons-material/Remove';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';
import { CustomReportPdfAnnotation, CustomReportPdfAnnotationType } from './customReportPdfAnnotations';

interface CustomReportPdfAnnotationEditorProps {
  pdfBlob?: Blob;
  isLoading?: boolean;
  annotations: CustomReportPdfAnnotation[];
  onAnnotationsChange: (annotations: CustomReportPdfAnnotation[]) => void;
  maxWidth?: number | string;
  defaultLogoDataUri?: string;
}

type Interaction =
  | { type: 'move'; annotationId: string; offsetX: number; offsetY: number }
  | { type: 'resize'; annotationId: string };

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const DEFAULT_TEXT_COLOR = '#111827';
const DEFAULT_TEXT_FONT_SIZE = 12;
const MIN_TEXT_FONT_SIZE = 6;
const MAX_TEXT_FONT_SIZE = 48;
const TEXT_FONT_SIZE_STEP = 1;
const isTextLike = (type: CustomReportPdfAnnotationType) => type === 'text' || type === 'signature';

const round = (value: number) => Number(value.toFixed(2));

const defaultAnnotationSize = (type: CustomReportPdfAnnotationType) => {
  switch (type) {
    case 'checkbox':
      return { width: 4, height: 3 };
    case 'signature':
      return { width: 24, height: 5 };
    case 'whiteout':
      return { width: 18, height: 5 };
    case 'image':
      return { width: 20, height: 8 };
    default:
      return { width: 22, height: 5 };
  }
};

const annotationBackground = (type: CustomReportPdfAnnotationType) => {
  switch (type) {
    case 'whiteout':
      return '#fff';
    default:
      return 'transparent';
  }
};

const newAnnotation = (
  type: CustomReportPdfAnnotationType,
  page: number,
  x: number,
  y: number,
  value?: string,
): CustomReportPdfAnnotation => {
  const { width, height } = defaultAnnotationSize(type);

  return {
    id: `pdf_annotation_${Date.now()}_${Math.round(Math.random() * 100000)}`,
    page,
    type,
    x: round(clamp(x, 0, 100 - width)),
    y: round(clamp(y, 0, 100 - height)),
    width,
    height,
    value: type === 'checkbox' ? 'true' : value ?? '',
  };
};

const CustomReportPdfAnnotationEditor = ({
  pdfBlob,
  isLoading = false,
  annotations,
  onAnnotationsChange,
  maxWidth = 980,
  defaultLogoDataUri,
}: CustomReportPdfAnnotationEditorProps) => {
  const pageRef = React.useRef<HTMLDivElement | null>(null);
  const [objectUrl, setObjectUrl] = React.useState<string>();
  const [pageSizes, setPageSizes] = React.useState<{ width: number; height: number }[]>([]);
  const [page, setPage] = React.useState(1);
  const [mode, setMode] = React.useState<CustomReportPdfAnnotationType>('text');
  const [selectedAnnotationId, setSelectedAnnotationId] = React.useState<string>();
  const [interaction, setInteraction] = React.useState<Interaction>();
  const [uploadedLogoDataUri, setUploadedLogoDataUri] = React.useState<string>();
  const [pendingFocusAnnotationId, setPendingFocusAnnotationId] = React.useState<string>();
  const [defaultTextColor, setDefaultTextColor] = React.useState(DEFAULT_TEXT_COLOR);
  const [defaultFontSize, setDefaultFontSize] = React.useState(DEFAULT_TEXT_FONT_SIZE);
  const textInputRefs = React.useRef<Record<string, HTMLTextAreaElement | null>>({});
  const logoDataUri = uploadedLogoDataUri ?? defaultLogoDataUri;

  React.useEffect(() => {
    if (!pdfBlob) {
      setObjectUrl(undefined);
      setPageSizes([]);
      return undefined;
    }

    let active = true;
    const nextUrl = URL.createObjectURL(pdfBlob);
    setObjectUrl(nextUrl);

    pdfBlob.arrayBuffer()
      .then(async (arrayBuffer) => {
        const pdfLib = await import('pdf-lib');
        const pdfDoc = await pdfLib.PDFDocument.load(arrayBuffer);
        return pdfDoc.getPages().map((pdfPage) => {
          const size = pdfPage.getSize();
          return { width: size.width, height: size.height };
        });
      })
      .then((sizes) => {
        if (!active) return;
        setPageSizes(sizes);
        setPage((currentPage) => clamp(currentPage, 1, Math.max(1, sizes.length)));
      })
      .catch(() => {
        if (active) {
          setPageSizes([]);
        }
      });

    return () => {
      active = false;
      URL.revokeObjectURL(nextUrl);
    };
  }, [pdfBlob]);

  const getPointerPercent = React.useCallback((clientX: number, clientY: number) => {
    const rect = pageRef.current?.getBoundingClientRect();
    if (!rect) return undefined;

    return {
      x: clamp(((clientX - rect.left) / rect.width) * 100, 0, 100),
      y: clamp(((clientY - rect.top) / rect.height) * 100, 0, 100),
    };
  }, []);

  const updateAnnotation = (annotationId: string, patch: Partial<CustomReportPdfAnnotation>) => {
    onAnnotationsChange(annotations.map((annotation) => (
      annotation.id === annotationId ? { ...annotation, ...patch } : annotation
    )));
  };

  const addAnnotation = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      event.target instanceof HTMLInputElement
      || event.target instanceof HTMLTextAreaElement
      || event.target instanceof HTMLButtonElement
    ) {
      return;
    }

    const pointer = getPointerPercent(event.clientX, event.clientY);
    if (!pointer) return;
    if (mode === 'image' && !logoDataUri) return;

    const base = newAnnotation(mode, page, pointer.x, pointer.y, mode === 'image' ? logoDataUri : undefined);
    const annotation = isTextLike(mode)
      ? { ...base, color: defaultTextColor, fontSize: defaultFontSize }
      : base;
    onAnnotationsChange([...annotations, annotation]);
    setSelectedAnnotationId(annotation.id);
    if (annotation.type === 'text' || annotation.type === 'signature') {
      setPendingFocusAnnotationId(annotation.id);
    }
    if (mode !== 'text') {
      setMode('text');
    }
  };

  React.useEffect(() => {
    if (!pendingFocusAnnotationId) return;

    const input = textInputRefs.current[pendingFocusAnnotationId];
    if (!input) return;

    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
    setPendingFocusAnnotationId(undefined);
  }, [annotations, pendingFocusAnnotationId]);

  const handleLogoUpload = (file?: File) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') return;
      setUploadedLogoDataUri(reader.result);
      setMode('image');
    };
    reader.readAsDataURL(file);
  };

  const startMove = (event: React.PointerEvent<HTMLDivElement>, annotation: CustomReportPdfAnnotation, force = false) => {
    if (
      !force
      && (
        event.target instanceof HTMLInputElement
        || event.target instanceof HTMLTextAreaElement
        || event.target instanceof HTMLButtonElement
      )
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const pointer = getPointerPercent(event.clientX, event.clientY);
    if (!pointer) return;

    setSelectedAnnotationId(annotation.id);
    setInteraction({
      type: 'move',
      annotationId: annotation.id,
      offsetX: pointer.x - annotation.x,
      offsetY: pointer.y - annotation.y,
    });
  };

  const startResize = (event: React.PointerEvent<HTMLDivElement>, annotation: CustomReportPdfAnnotation) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedAnnotationId(annotation.id);
    setInteraction({
      type: 'resize',
      annotationId: annotation.id,
    });
  };

  React.useEffect(() => {
    if (!interaction) return undefined;

    const handleMove = (event: PointerEvent) => {
      const pointer = getPointerPercent(event.clientX, event.clientY);
      const annotation = annotations.find((item) => item.id === interaction.annotationId);
      if (!pointer || !annotation) return;

      if (interaction.type === 'resize') {
        updateAnnotation(annotation.id, {
          width: round(clamp(pointer.x - annotation.x, 2, 100 - annotation.x)),
          height: round(clamp(pointer.y - annotation.y, 2, 100 - annotation.y)),
        });
        return;
      }

      updateAnnotation(annotation.id, {
        x: round(clamp(pointer.x - interaction.offsetX, 0, 100 - annotation.width)),
        y: round(clamp(pointer.y - interaction.offsetY, 0, 100 - annotation.height)),
      });
    };

    const handleUp = () => setInteraction(undefined);

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [annotations, getPointerPercent, interaction]);

  const deleteSelectedAnnotation = () => {
    if (!selectedAnnotationId) return;
    onAnnotationsChange(annotations.filter((annotation) => annotation.id !== selectedAnnotationId));
    setSelectedAnnotationId(undefined);
  };

  const selectedAnnotation = annotations.find((annotation) => annotation.id === selectedAnnotationId);
  const selectedTextAnnotation = selectedAnnotation && isTextLike(selectedAnnotation.type) ? selectedAnnotation : undefined;
  const activeTextColor = selectedTextAnnotation?.color ?? defaultTextColor;
  const activeFontSize = selectedTextAnnotation?.fontSize ?? defaultFontSize;

  const applyTextColor = (color: string) => {
    setDefaultTextColor(color);
    if (selectedTextAnnotation) updateAnnotation(selectedTextAnnotation.id, { color });
  };

  const applyFontSize = (size: number) => {
    const next = clamp(Math.round(size), MIN_TEXT_FONT_SIZE, MAX_TEXT_FONT_SIZE);
    setDefaultFontSize(next);
    if (selectedTextAnnotation) updateAnnotation(selectedTextAnnotation.id, { fontSize: next });
  };

  const visibleAnnotations = annotations.filter((annotation) => annotation.page === page);
  const currentPageSize = pageSizes[page - 1];
  const pageAspectRatio = currentPageSize ? `${currentPageSize.width} / ${currentPageSize.height}` : '1 / 1.414';

  const updateTextValue = (annotation: CustomReportPdfAnnotation, value: string) => {
    const lines = value.split('\n');
    const longestLineLength = Math.max(...lines.map((line) => line.length), 1);
    const defaultSize = defaultAnnotationSize(annotation.type);

    updateAnnotation(annotation.id, {
      value,
      width: round(clamp(Math.max(defaultSize.width, longestLineLength * 0.7 + 2), 2, 100 - annotation.x)),
      height: round(clamp(Math.max(defaultSize.height, lines.length * 2.4 + 2), 2, 100 - annotation.y)),
    });
  };

  const renderAnnotationContent = (annotation: CustomReportPdfAnnotation) => {
    if (annotation.type === 'checkbox') {
      return (
        <input
          aria-label="Check"
          type="checkbox"
          checked={annotation.value === 'true'}
          onChange={(event) => updateAnnotation(annotation.id, { value: event.target.checked ? 'true' : 'false' })}
          style={{ width: '100%', height: '100%', margin: 0 }}
        />
      );
    }

    if (annotation.type === 'whiteout') {
      return <Box sx={{ width: '100%', height: '100%', bgcolor: '#fff' }} />;
    }

    if (annotation.type === 'image') {
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.7)',
          }}
        >
          {annotation.value ? (
            <img
              src={annotation.value}
              alt="Logo"
              style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
            />
          ) : null}
        </Box>
      );
    }

    return (
      <textarea
        ref={(element) => {
          textInputRefs.current[annotation.id] = element;
        }}
        aria-label={annotation.type === 'signature' ? 'Signature' : 'Text'}
        placeholder={annotation.type === 'signature' ? 'Signature' : undefined}
        value={annotation.value}
        onFocus={() => setSelectedAnnotationId(annotation.id)}
        onChange={(event) => updateTextValue(annotation, event.target.value)}
        style={{
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
          border: 'none',
          background: 'transparent',
          color: annotation.color ?? DEFAULT_TEXT_COLOR,
          caretColor: annotation.color ?? DEFAULT_TEXT_COLOR,
          fontSize: annotation.fontSize ?? DEFAULT_TEXT_FONT_SIZE,
          fontFamily: annotation.type === 'signature' ? '"Segoe Script", "Brush Script MT", cursive' : 'inherit',
          padding: 0,
          resize: 'none',
          outline: 'none',
          overflow: 'hidden',
        }}
      />
    );
  };

  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ButtonGroup size="small" color="secondary">
            <Button
              variant={mode === 'text' ? 'contained' : 'outlined'}
              startIcon={<TextFieldsIcon />}
              onClick={() => setMode('text')}
            >
              Text
            </Button>
            <Button
              variant={mode === 'checkbox' ? 'contained' : 'outlined'}
              startIcon={<CheckBoxIcon />}
              onClick={() => setMode('checkbox')}
            >
              Check
            </Button>
            <Button
              variant={mode === 'signature' ? 'contained' : 'outlined'}
              startIcon={<BorderColorIcon />}
              onClick={() => setMode('signature')}
            >
              Sign
            </Button>
            <Button
              variant={mode === 'whiteout' ? 'contained' : 'outlined'}
              startIcon={<CropSquareIcon />}
              onClick={() => setMode('whiteout')}
            >
              Remove
            </Button>
            <Button
              variant={mode === 'image' ? 'contained' : 'outlined'}
              startIcon={<ImageIcon />}
              disabled={!logoDataUri}
              onClick={() => setMode('image')}
            >
              Logo
            </Button>
          </ButtonGroup>
          <Button
            component="label"
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={<UploadFileIcon />}
          >
            Upload logo
            <input
              hidden
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              onChange={(event) => handleLogoUpload(event.target.files?.[0])}
            />
          </Button>
          <TextField
            label={i18n._('Page')}
            size="small"
            type="number"
            value={page}
            onChange={(event) => setPage(Math.max(1, Number(event.target.value) || 1))}
            sx={{ width: 96 }}
            inputProps={{ min: 1 }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title={selectedTextAnnotation ? 'Text colour (selected)' : 'Text colour for new text'}>
            <Box
              component="label"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0.75,
                height: 32,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                cursor: 'pointer',
              }}
            >
              <TextFieldsIcon fontSize="small" color="secondary" />
              <Box sx={{ width: 18, height: 18, borderRadius: 0.5, bgcolor: activeTextColor, border: '1px solid', borderColor: 'divider' }} />
              <Box
                component="input"
                type="color"
                value={activeTextColor}
                onChange={(event) => applyTextColor((event.target as HTMLInputElement).value)}
                sx={{ width: 0, height: 0, opacity: 0, position: 'absolute', pointerEvents: 'none' }}
              />
            </Box>
          </Tooltip>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={i18n._('Decrease text size')}>
              <IconButton size="small" color="secondary" onClick={() => applyFontSize(activeFontSize - TEXT_FONT_SIZE_STEP)}>
                <RemoveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="caption" sx={{ minWidth: 28, textAlign: 'center' }}>{activeFontSize}</Typography>
            <Tooltip title={i18n._('Increase text size')}>
              <IconButton size="small" color="secondary" onClick={() => applyFontSize(activeFontSize + TEXT_FONT_SIZE_STEP)}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Tooltip title={i18n._('Delete selected edit')}>
            <span>
              <IconButton color="error" disabled={!selectedAnnotationId} onClick={deleteSelectedAnnotation}>
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ bgcolor: '#e9edf3', borderRadius: 1, p: 2, display: 'flex', justifyContent: 'center' }}>
        <Box
          ref={pageRef}
          onClick={addAnnotation}
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth,
            aspectRatio: pageAspectRatio,
            border: '1px solid #e2e8f0',
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: '#fff',
            boxShadow: '0 2px 14px rgba(15, 23, 42, 0.14)',
            cursor: mode === 'checkbox' ? 'crosshair' : 'text',
          }}
        >
          {objectUrl ? (
            <object
              key={`${objectUrl}-${page}`}
              data={`${objectUrl}#toolbar=0&navpanes=0&scrollbar=0&page=${page}&view=Fit`}
              type="application/pdf"
              aria-label="Custom report PDF"
              style={{ width: '100%', height: '100%', display: 'block', pointerEvents: 'none' }}
            >
              <Typography variant="body2" sx={{ p: 2 }}>{i18n._('PDF preview unavailable.')}</Typography>
            </object>
          ) : (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {isLoading ? <CircularProgress size={28} /> : <Typography variant="body2">{i18n._('PDF preview unavailable.')}</Typography>}
            </Box>
          )}
          <Box sx={{ position: 'absolute', inset: 0 }}>
            {visibleAnnotations.map((annotation) => {
              const selected = annotation.id === selectedAnnotationId;

              return (
                <Box
                  key={annotation.id}
                  onPointerDown={(event) => startMove(event, annotation)}
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedAnnotationId(annotation.id);
                  }}
                  sx={{
                    position: 'absolute',
                    left: `${annotation.x}%`,
                    top: `${annotation.y}%`,
                    width: `${annotation.width}%`,
                    height: `${annotation.height}%`,
                    outline: selected && annotation.type !== 'text' && annotation.type !== 'signature' ? '2px solid' : '1px solid transparent',
                    outlineColor: selected && annotation.type !== 'text' && annotation.type !== 'signature' ? 'secondary.main' : 'transparent',
                    bgcolor: annotationBackground(annotation.type),
                    cursor: 'move',
                  }}
                >
                  {selected && (
                    <Box
                      aria-label="Move edit"
                      role="button"
                      tabIndex={0}
                      onPointerDown={(event) => startMove(event, annotation, true)}
                      onClick={(event) => event.stopPropagation()}
                      sx={{
                        position: 'absolute',
                        top: -13,
                        right: -13,
                        zIndex: 2,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'secondary.main',
                        color: 'secondary.contrastText',
                        boxShadow: 2,
                        cursor: interaction?.annotationId === annotation.id ? 'grabbing' : 'grab',
                      }}
                    >
                      <OpenWithIcon sx={{ fontSize: 15 }} />
                    </Box>
                  )}
                  {selected && (
                    <Box
                      aria-label="Resize edit"
                      role="button"
                      tabIndex={0}
                      onPointerDown={(event) => startResize(event, annotation)}
                      onClick={(event) => event.stopPropagation()}
                      sx={{
                        position: 'absolute',
                        right: -6,
                        bottom: -6,
                        zIndex: 2,
                        width: 14,
                        height: 14,
                        borderRadius: 0.5,
                        bgcolor: 'secondary.main',
                        border: '2px solid',
                        borderColor: 'background.paper',
                        boxShadow: 2,
                        cursor: 'nwse-resize',
                      }}
                    />
                  )}
                  {renderAnnotationContent(annotation)}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Stack>
  );
};

export default CustomReportPdfAnnotationEditor;
